// Lecture du format publié des feuilles d'analyse (plugin `analysis` du noyau, lot M7) dans la notation du moteur v2.
//
// Format publié : départ « f(x) = E », cible « f′(x) » (dérivée) ou « F(x) » (primitive), lignes à TÊTE
// (« f′(x) = … », « u(x) = … », « u′(x) = … »). Le moteur v2 calcule une dérivée avec le nœud « (E)′ » et ses
// packs (`analyse.derivees`) : une feuille de dérivée devient la feuille native « f′(x) = (E)′ » à simplifier.
// Les têtes (« f(x) », « u′(x) ») se lisent comme des noms. Aucune pédagogie ici : c'est le format des données.

const HEAD = /([A-Za-z](?:′|')*)\(([a-z])\)/gu;

/** Têtes « nom(variable) » écrites dans les textes du modèle (« f′(x) », « u(x) », « G(x) »). */
export function analysisHeads(texts) {
  const out = new Set();
  for (const t of texts)
    for (const m of String(t ?? '').matchAll(HEAD)) out.add(m[0].replace(/'/g, '′'));
  return [...out];
}

/**
 * Définition « f(x) = E » ou « v′(t) = E » → { name: 'f', primes: 0, v: 'x', expr: 'E' } (null si ce n'est pas ça).
 */
export function definitionOf(text) {
  const m = /^\s*([A-Za-z])((?:′|')*)\(([a-z])\)\s*=\s*(.+)$/u.exec(String(text ?? ''));
  return m ? { name: m[1], primes: m[2].length, v: m[3], expr: m[4].trim() } : null;
}

/** Toutes les têtes qu'un élève peut écrire : une lettre (ni e, ni la variable), 0 à 2 primes, « (variable) ». */
export function allHeads(v) {
  const out = [];
  for (const c of 'abcdfghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ') {
    if (c === v) continue;
    for (const p of ['', '′', '′′']) out.push(`${c}${p}(${v})`);
  }
  return out;
}

const primesOf = (head) => (/^[A-Za-z]((?:′|')*)\(/u.exec(head)?.[1] ?? '').length;

/**
 * Modèle natif d'une feuille d'analyse publiée, ou le modèle tel quel.
 *  - dérivée (`differentiate`, cible « f′(x) » ou « f′′(x) ») : départ « f′(x) = (E)′ » (« f′′(x) = ((E)′)′ »),
 *    objectif `simplify` ;
 *  - primitive (`antiderivative`, cible « F(x) », départ « f(x) = E » ou « v′(t) = E ») : la feuille garde son
 *    départ ; le juge vérifie qu'une ligne « F(x) = R » a pour dérivée la fonction donnée.
 * Les têtes écrites dans les textes deviennent des noms de l'exercice (symbols).
 */
export function adaptAnalysisModel(model) {
  const k = model?.kernel;
  if (!k || k.plugin !== 'analysis') return model;
  const kind = k.goal?.kind;
  if (kind !== 'differentiate' && kind !== 'antiderivative') return model;
  const def = definitionOf(k.start);
  const target = k.goal.target;
  if (!def || typeof target !== 'string') return model;
  if (kind === 'antiderivative' && (k.condition || k.at)) return model;
  const heads = analysisHeads([
    k.start,
    k.answer,
    target,
    ...(k.path ?? []),
    ...(model.traps ?? []).map((t) => t.line),
  ]);
  const symbols = { ...(model.symbols ?? {}) };
  for (const h of heads) if (!symbols[h]) symbols[h] = {};
  const analysis = {
    kind: kind === 'differentiate' ? 'derive' : 'anti',
    name: def.name,
    primes: def.primes,
    v: def.v,
    expr: def.expr,
    heads: allHeads(def.v),
  };
  if (kind === 'antiderivative') return { ...model, symbols, kernel: { ...k, analysis } };
  const n = primesOf(target) - def.primes;
  if (n < 1 || n > 2) return model;
  const der = (e) => `(${e})′`;
  const startExpr = n === 2 ? der(der(def.expr)) : der(def.expr);
  return {
    ...model,
    symbols,
    kernel: {
      ...k,
      goal: { kind: 'simplify', target },
      start: `${target} = ${startExpr}`,
      analysis: { ...analysis, order: n },
    },
  };
}
